import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiWrench, FiShield, FiTool, FiSettings, FiTruck, FiZap, FiDroplet, FiRotateCw, FiCheckCircle, FiClock, FiDollarSign } = FiIcons;

const Services = () => {
  useEffect(() => {
    document.title = "Professional Auto Services - AutoFix Pro | Car Repair & Maintenance";
  }, []);

  const services = [
    {
      icon: FiWrench,
      title: 'Engine Repair & Diagnostics',
      description: 'Complete engine repair services including diagnostics, tune-ups, and overhauls',
      features: ['Computer diagnostics', 'Engine tune-ups', 'Timing belt replacement', 'Head gasket repair'],
      price: 'Starting at $89',
      image: 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=400&h=300&fit=crop'
    },
    {
      icon: FiShield,
      title: 'Brake Service & Repair',
      description: 'Professional brake inspection, repair, and replacement services',
      features: ['Brake pad replacement', 'Rotor resurfacing', 'Brake fluid service', 'ABS system repair'],
      price: 'Starting at $129',
      image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop'
    },
    {
      icon: FiDroplet,
      title: 'Oil Change & Lubrication',
      description: 'Quick and efficient oil change services with premium lubricants',
      features: ['Conventional oil change', 'Synthetic oil service', 'Filter replacement', 'Fluid top-off'],
      price: 'Starting at $39',
      image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop'
    },
    {
      icon: FiRotateCw,
      title: 'Tire Services',
      description: 'Complete tire services including installation, rotation, and balancing',
      features: ['Tire installation', 'Wheel balancing', 'Tire rotation', 'Alignment service'],
      price: 'Starting at $25',
      image: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=400&h=300&fit=crop'
    },
    {
      icon: FiSettings,
      title: 'Transmission Service',
      description: 'Expert transmission repair and maintenance services',
      features: ['Transmission flush', 'Clutch replacement', 'Transmission rebuild', 'Fluid service'],
      price: 'Starting at $159',
      image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=400&h=300&fit=crop'
    },
    {
      icon: FiZap,
      title: 'AC & Heating Service',
      description: 'Climate control system repair and maintenance',
      features: ['AC recharge', 'Compressor repair', 'Heater core service', 'System diagnostics'],
      price: 'Starting at $79',
      image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=400&h=300&fit=crop'
    }
  ];

  const processSteps = [
    {
      icon: FiCheckCircle,
      title: 'Free Inspection',
      description: 'We start with a comprehensive diagnostic to identify the issue'
    },
    {
      icon: FiDollarSign,
      title: 'Transparent Quote',
      description: 'Receive a detailed estimate with no hidden fees or surprises'
    },
    {
      icon: FiTool,
      title: 'Expert Repair',
      description: 'Our certified technicians perform the work using quality parts'
    },
    {
      icon: FiShield,
      title: 'Quality Guarantee',
      description: 'All work comes with our 12-month warranty for peace of mind'
    }
  ];

  const benefits = [
    'ASE Certified Technicians',
    'State-of-the-Art Equipment',
    '12-Month Warranty',
    'Competitive Pricing',
    'Quick Turnaround',
    'Quality Parts & Materials',
    'Free Estimates',
    '24/7 Emergency Service'
  ];

  return (
    <div className="min-h-screen">
      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-black mb-4">Our Professional Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive automotive repair and maintenance services for all makes and models
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative h-48">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                  <div className="absolute top-4 left-4">
                    <div className="bg-red-600 p-3 rounded-full">
                      <SafeIcon icon={service.icon} className="text-white text-xl" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white px-3 py-1 rounded-full">
                    <span className="text-red-600 font-semibold text-sm">{service.price}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors">
                    Get Quote
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-black mb-4">Our Service Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, transparent, and reliable - here's how we take care of your vehicle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={step.icon} className="text-red-600 text-2xl" />
                </div>
                <div className="bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-6">Why Choose Our Services?</h2>
              <p className="text-xl mb-8">
                We're committed to providing the highest quality automotive services with 
                exceptional customer care and competitive pricing.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-white flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=600&h=400&fit=crop"
                alt="Professional auto service"
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emergency Service Section */}
      <section className="py-16 bg-black text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-red-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiClock} className="text-3xl" />
            </div>
            <h2 className="text-4xl font-bold mb-4">24/7 Emergency Service</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
              Car trouble doesn't wait for business hours. Our emergency service team is available 
              around the clock to get you back on the road safely.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:(555)123-4567"
                className="bg-red-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors"
              >
                Call Emergency: (555) 123-4567
              </a>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-white hover:text-black transition-colors">
                Request Towing
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;